// src/components/admin/AvisosAdmin.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Box, Typography, CircularProgress, IconButton, Paper, List, ListItem,
    ListItemText, ListItemSecondaryAction, Tooltip, Button, Stack, Chip, Tabs, Tab,
    Pagination, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, MenuItem, Snackbar, FormControlLabel, Checkbox
} from "@mui/material";
import { CheckCircleOutline, Refresh, DoneAll, OpenInNew, Send } from "@mui/icons-material";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

const PAGE_SIZE_DEFAULT = 10;
const TIPOS = ["OTRO", "ALTA", "CITA", "CURSO", "PAGO", "SUBVENCION", "CAMBIO_DATOS"]; // ajusta a tu enum

function timeAgo(tsIso) {
    if (!tsIso) return "";
    const d = new Date(tsIso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "hace unos segundos";
    if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
    return d.toLocaleString();
}

// Normalizador de cada aviso (snake_case -> camelCase)
const normAviso = (a) => ({
    id: a.id,
    destinatarioTipo: a.destinatario_tipo,
    destinatarioCodigo: a.destinatario_codigo,
    colegiado: a.colegido ?? a.colegiado ?? null,
    tipo: a.tipo,
    entidad: a.entidad,
    entidadId: a.entidad_id ?? a.entidadId ?? null,
    estadoRelacionado: a.estado_relacionado ?? a.estadoRelacionado ?? null,
    titulo: a.titulo,
    contenido: a.contenido,
    urlAccion: a.url_accion ?? a.urlAccion ?? null,
    leido: a.leido,
    grupoAdmin: a.grupo_admin ?? a.grupoAdmin ?? null,
    creadoEl: a.creado_el ?? a.creadoEl,
});

// Normalizador de la respuesta Page
const normPage = (p, fallbackSize) => ({
    content: Array.isArray(p?.content) ? p.content.map(normAviso) : [],
    totalPages: p?.total_pages ?? p?.totalPages ?? 0,
    totalElements: p?.total_elements ?? p?.totalElements ?? 0,
    number: p?.number ?? 0, // 0-based
    size: p?.size ?? fallbackSize ?? PAGE_SIZE_DEFAULT,
    first: !!p?.first,
    last: !!p?.last,
});

export default function AvisosAdmin() {
    const { usuario, token } = useAuth(); // usuario.rol === 'ADMIN'
    const [data, setData] = useState({ content: [], totalPages: 0, totalElements: 0, number: 0, size: PAGE_SIZE_DEFAULT });
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState("");
    const [tab, setTab] = useState(0); // 0=Todos, 1=No leídos
    const [page, setPage] = useState(1); // UI 1-based
    const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);

    const [openSend, setOpenSend] = useState(false);
    const [sendForm, setSendForm] = useState({
        enviarATodos: false,
        destinatarios: "", // "123,456,789"
        titulo: "",
        contenido: "",
        urlAccion: "",
        tipo: "OTRO",
        entidad: "",
        entidadId: "",
        estado: "INFO",
    });

    const API = process.env.REACT_APP_API_URL || "http://localhost:8080";
    const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const notifyBadge = () => {
        window.dispatchEvent(new CustomEvent("avisos:refresh"));
    };

    const fetchAvisos = async (opts = {}) => {
        try {
            setLoading(true);
            const soloNoLeidos = tab === 1;
            const params = {
                soloNoLeidos,
                page: (opts.page ?? (page - 1)),
                size: (opts.size ?? pageSize),
                sort: "creadoEl,DESC",
            };
            const res = await axios.get(`${API}/api/avisos/admin`, { params, headers: auth });
            setData(normPage(res.data, params.size));
        } catch (e) {
            console.error("Error cargando avisos admin:", e);
            setMsg("Error al cargar avisos.");
        } finally {
            setLoading(false);
        }
    };

    const marcarComoLeido = async (id) => {
        try {
            await axios.post(`${API}/api/avisos/admin/${id}/leido`, {}, { headers: auth });
            setData((prev) => ({
                ...prev,
                content: prev.content.map((a) => (a.id === id ? { ...a, leido: true } : a)),
            }));
            // Refrescar listado
            fetchAvisos({ page: page - 1, size: pageSize });
            notifyBadge();
        } catch (e) {
            console.error(e);
            setMsg("No se pudo marcar como leído.");
        }
    };

    // OJO: si no tienes endpoint admin para “marcar todos”, elimina este botón o apúntalo al correcto.
    const marcarTodos = async () => {
        try {
            // Cambia a tu endpoint real si existe la acción en admin:
            await axios.post(`${API}/api/avisos/marcar-todos-leidos`, {}, { headers: auth });
            setTab(0);
            setPage(1);
            fetchAvisos({ page: 0, size: pageSize });
            notifyBadge();
        } catch (e) {
            console.error(e);
            setMsg("No se pudieron marcar todos como leídos.");
        }
    };

    useEffect(() => {
        fetchAvisos();
    }, [tab, page, pageSize, token]);

    const content = data?.content ?? [];

    const handleOpenSend = () => setOpenSend(true);
    const handleCloseSend = () => setOpenSend(false);
    const handleChangeSend = (e) => setSendForm((s) => ({ ...s, [e.target.name]: e.target.value }));
    const handleToggleEnviarATodos = (e) => setSendForm((s) => ({ ...s, enviarATodos: e.target.checked }));

    const enviarAvisos = async () => {
        try {
            if (!sendForm.titulo || !sendForm.contenido) {
                setMsg("Título y contenido son obligatorios.");
                return;
            }

            if (sendForm.enviarATodos) {
                const payload = {
                    entidad: sendForm.entidad || null,
                    entidadId: sendForm.entidadId || null,
                    titulo: sendForm.titulo,
                    contenido: sendForm.contenido,
                    urlAccion: sendForm.urlAccion || null,
                };
                await axios.post(`${API}/api/avisos/enviar-a-todos`, payload, { headers: auth });
                setMsg("Aviso GENERAL enviado a todos los colegiados.");
            } else {
                const destinatarios = sendForm.destinatarios
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .map(Number)
                    .filter((n) => Number.isInteger(n));

                if (!destinatarios.length) {
                    setMsg("Debes indicar al menos un código de colegiado o marca 'Enviar a todos'.");
                    return;
                }

                const payload = {
                    destinatarios,
                    titulo: sendForm.titulo,
                    contenido: sendForm.contenido,
                    urlAccion: sendForm.urlAccion || null,
                    tipo: sendForm.tipo || "OTRO",
                    entidad: sendForm.entidad || null,
                    entidadId: sendForm.entidadId || null,
                    estado: sendForm.estado || "INFO",
                };

                await axios.post(`${API}/api/avisos/enviar`, payload, { headers: auth });
                setMsg("Aviso(s) enviado(s).");
            }

            setOpenSend(false);
            fetchAvisos({ page: page - 1, size: pageSize });
            notifyBadge();
        } catch (e) {
            console.error(e);
            setMsg("No se pudieron enviar los avisos.");
        }
    };

    const isAdmin = String(usuario?.rol || "").toUpperCase() === "ADMIN";

    // Cálculo de “mostrando X–Y de Z”
    const from = data.totalElements === 0 ? 0 : (data.number * data.size) + 1;
    const to = data.totalElements === 0 ? 0 : Math.min((data.number + 1) * data.size, data.totalElements);

    return (
        <Box p={3}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                <Typography variant="h5">Avisos (Admin)</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    {/* Selector de tamaño de página */}
                    <TextField
                        size="small"
                        select
                        label="Por página"
                        value={pageSize}
                        onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                        sx={{ width: 120 }}
                    >
                        {[10, 20, 30, 50].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                    </TextField>

                    {isAdmin && (
                        <Button size="small" startIcon={<Send />} variant="contained" onClick={handleOpenSend}>
                            Enviar aviso
                        </Button>
                    )}
                    <Button size="small" startIcon={<DoneAll />} onClick={marcarTodos} variant="outlined">
                        Marcar todos leídos
                    </Button>
                    <IconButton onClick={() => fetchAvisos()}>
                        <Refresh />
                    </IconButton>
                </Stack>
            </Stack>

            <Tabs
                value={tab}
                onChange={(_, v) => { setTab(v); setPage(1); }}
                sx={{ mb: 2 }}
            >
                <Tab label="Todos" />
                <Tab label="No leídos" />
            </Tabs>

            {loading ? (
                <CircularProgress />
            ) : content.length === 0 ? (
                <Typography>No tienes avisos.</Typography>
            ) : (
                <Paper elevation={2}>
                    <List>
                        {content.map((av) => (
                            <ListItem
                                key={av.id}
                                divider
                                sx={{ bgcolor: av.leido ? "grey.100" : "white", alignItems: "flex-start" }}
                            >
                                <ListItemText
                                    primary={
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            <Typography fontWeight={600}>{av.titulo}</Typography>
                                            {av.estadoRelacionado && <Chip size="small" label={av.estadoRelacionado} />}
                                            {av.tipo && <Chip size="small" variant="outlined" label={av.tipo} />}
                                            <Typography variant="body2" color="text.secondary">
                                                {timeAgo(av.creadoEl)}
                                            </Typography>
                                        </Stack>
                                    }
                                    secondary={
                                        <>
                                            <Typography variant="body2">{av.contenido}</Typography>
                                            {av.entidad && av.entidadId && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Ref: {av.entidad} #{av.entidadId}
                                                </Typography>
                                            )}
                                        </>
                                    }
                                />
                                <ListItemSecondaryAction>
                                    <Stack direction="row" spacing={1}>
                                        {av.urlAccion && (
                                            <Tooltip title="Ir">
                                                <IconButton onClick={() => window.location.assign(av.urlAccion)} color="inherit">
                                                    <OpenInNew />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {av.leido ? (
                                            <Tooltip title="Leído">
                                                <CheckCircleOutline color="success" />
                                            </Tooltip>
                                        ) : (
                                            <Tooltip title="Marcar como leído">
                                                <IconButton onClick={() => marcarComoLeido(av.id)} color="primary">
                                                    <CheckCircleOutline />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Stack>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>

                    <Divider />

                    <Box p={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                        <Typography variant="body2">
                            Mostrando {from}-{to} de {data.totalElements}
                        </Typography>
                        <Pagination
                            count={data.totalPages || 1}
                            page={page}
                            onChange={(_, p) => setPage(p)}
                            color="primary"
                        />
                    </Box>
                </Paper>
            )}

            {/* Diálogo para enviar avisos */}
            <Dialog open={openSend} onClose={handleCloseSend} fullWidth maxWidth="sm">
                <DialogTitle>Enviar aviso a colegiados</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Stack spacing={2}>
                        <FormControlLabel
                            control={<Checkbox checked={sendForm.enviarATodos} onChange={handleToggleEnviarATodos} />}
                            label="Enviar a todos los colegiados (tipo GENERAL)"
                        />

                        {!sendForm.enviarATodos && (
                            <TextField
                                label="Códigos de colegiado"
                                helperText="Separados por coma. Ej: 101, 205, 333"
                                name="destinatarios"
                                value={sendForm.destinatarios}
                                onChange={handleChangeSend}
                                fullWidth
                            />
                        )}

                        <TextField label="Título" name="titulo" value={sendForm.titulo} onChange={handleChangeSend} fullWidth />
                        <TextField
                            label="Contenido" name="contenido" value={sendForm.contenido}
                            onChange={handleChangeSend} multiline minRows={3} fullWidth
                        />

                        {!sendForm.enviarATodos && (
                            <Stack direction="row" spacing={2}>
                                <TextField label="Tipo" name="tipo" value={sendForm.tipo} onChange={handleChangeSend} select sx={{ minWidth: 180 }}>
                                    {TIPOS.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                </TextField>
                                <TextField label="Estado (opcional)" name="estado" value={sendForm.estado} onChange={handleChangeSend} />
                            </Stack>
                        )}

                        <Stack direction="row" spacing={2}>
                            <TextField label="Entidad (opcional)" name="entidad" value={sendForm.entidad} onChange={handleChangeSend} />
                            <TextField label="Entidad ID (opcional)" name="entidadId" value={sendForm.entidadId} onChange={handleChangeSend} />
                        </Stack>

                        <TextField
                            label="URL de acción (opcional)" name="urlAccion" value={sendForm.urlAccion}
                            onChange={handleChangeSend} placeholder="/app/cursos/inscripciones"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseSend}>Cancelar</Button>
                    <Button onClick={enviarAvisos} variant="contained" startIcon={<Send />}>Enviar</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={!!msg} autoHideDuration={4000} onClose={() => setMsg("")} message={msg} />
        </Box>
    );
}
